import BaseComponent from '@js/BaseComponent'

import {
    isDarkMode,
    onIsDarkMode,
    isHighContrast,
    onIsHighContrast,
    isForcedColors,
    onIsForcedColors,
    breakpoint,
    onBreakpoint
} from '@js/mediaQueries'

/**
 * Controller for the Aurora effect
 *
 * @extends BaseComponent
 */
export default class Aurora extends BaseComponent {

    /** Sets how often to refresh the Aurora effect (in ms) */
    static refreshInterval = 60000

    static gradientShearMatrix = {
        m11: 1,
        m12: 0,     // vertical shear
        m21: -0.85, // horizontal shear
        m22: 1,
        m41: 0,
        m42: 0
    }

    /** The amount of the full gradient to show in the viewport width */
    get gradientFraction() {
        if (['xsmall', 'small'].includes(breakpoint())) {
            return 1 / 6
        }

        return 1 / 4
    }

    /** Light and dark mode color palettes */
    get colors() {
        return {
            lightMode: [
                '#4111c5',
                '#9cd5ff',
                '#ffc368',
                '#2afdcc',
                '#d930d9'
            ],
            darkMode: [
                '#e00094',
                '#8215ee',
                '#c96b13',
                '#015C9d',
                '#066952'
            ]
        }
    }

    inInView = false

    /** The color palette after theme selection and color repeating */
    colorPalette = []

    /** A timeout timeoutID for use with clearTimeout */
    drawTimeout

    /** The opacity to render the Aurora at the top of the gradient */
    get maxOpacity() {
        if (isHighContrast()) {
            return 0.2
        }

        return isDarkMode ? 0.5 : 0.65
    }

    constructor(element) {
        super(element)

        this.ctx = this.element.getContext('2d')

        this.setColorPalette()
        this.setCanvasSize()

        onIsDarkMode(() => {
            this.setColorPalette()
            this.draw(true)
        })

        onIsHighContrast(() => {
            this.draw(true)
        })

        onIsForcedColors(isForced => {
            if (isForced) {
                this.clearEnqueuedDraw()
            } else {
                this.draw(true)
            }
        })

        onBreakpoint(() => {
            this.draw(true)
        })

        window.addEventListener('resize', () => {
            this.setCanvasSize()
            this.draw(true)
        })

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.clearEnqueuedDraw()
            } else if (document.visibilityState === 'visible') {
                this.draw()
            }
        })


        if (!isForcedColors()) {
            this.draw()
        }
    }

    /** Updates the width and height of the canvas element */
    setCanvasSize = () => {
        this.ctx.canvas.width = this.element.getBoundingClientRect().width
        this.ctx.canvas.height = this.element.getBoundingClientRect().height
    }

    /** Prepares the value of colorPalette based on theme and after repeats */
    setColorPalette = () => {
        const colors =
            isDarkMode() ?
            this.colors.darkMode :
            this.colors.lightMode

        this.colorPalette = colors
            .concat(colors)
            .concat(colors)
            .concat(colors[0])
    }

    /** Creates the CanvasGradient object used for the Aurora colors */
    createGradient = () => {
        const factor = this.getTimeOfDayFactor()

        const repeatCount = 3
        const partialWidth = this.ctx.canvas.width / this.gradientFraction
        const fullWidth = partialWidth * repeatCount
        const realignmentStart = (fullWidth / -repeatCount) + (this.ctx.canvas.width / 2)
        const factorOffset = factor * partialWidth

        return this.ctx.createLinearGradient(
            realignmentStart - factorOffset,
            0,
            realignmentStart - factorOffset + fullWidth,
            0
        )
    }

    /**
     * Draw the canvas contents.
     *
     * @param {boolean} clear - When set to true, clears the current setTimeout
     */
    draw = (clear) => {
        if (clear) {
            this.clearEnqueuedDraw()
        }

        if (isForcedColors()) {
            return
        }

        const gradient = this.createGradient()

        this.colorPalette.forEach((color, idx, colors) => {
            gradient.addColorStop(
                idx / (colors.length - 1),
                color
            )
        })

        this.ctx.setTransform(Aurora.gradientShearMatrix)
        this.ctx.fillStyle = gradient
        this.ctx.fillRect(
            this.ctx.canvas.width / -2,
            0,
            this.ctx.canvas.width * 3,
            this.ctx.canvas.height
        )
        this.ctx.resetTransform()

        this.drawMask()


        setTimeout(this.draw, Aurora.refreshInterval)
    }

    /** Draws the gradient that masks the Aurora out */
    drawMask = () => {
        const mask = this.ctx.createLinearGradient(
            0,
            this.ctx.canvas.height,
            this.ctx.canvas.height * -0.05,
            0
        )

        mask.addColorStop(0, 'rgba(0.0, 0.0, 0.0, 0.0)')
        mask.addColorStop(1, `rgba(0.0, 0.0, 0.0, ${this.maxOpacity})`)
        this.ctx.fillStyle = mask
        this.ctx.globalCompositeOperation = 'destination-in';
        this.ctx.fillRect(
            0,
            0,
            this.ctx.canvas.width,
            this.ctx.canvas.height
        )
        this.ctx.globalCompositeOperation = 'source-over'
    }

    /**
     * Calculates the percent complete of the current day and returns it
     * normalized to the range [0..1].
     * @type {float}
     */
    getTimeOfDayFactor() {
        const secondsPerDay = 60 * 60 * 24

        const now = new Date()
        const seconds =
            now.getHours() * 3600 +
            now.getMinutes() * 60 +
            now.getSeconds()

        // If by some weird quirk of time and JS, seconds is ever greater than
        // secondsPerDay, cap it at "midnight"
        return Math.min(seconds / secondsPerDay, 1.0)
    }

    /** Clears the next draw call from setTimeout */
    clearEnqueuedDraw = () => {
        clearTimeout(this.drawTimeout)
    }
}

Aurora.create('.aurora')
